import { IAuditLog } from 'src/app/admin/audit-log/audit-log.model';

/** Feature dropdown — the shape `getFeatureDropDown()` returns. */
export const AUDIT_FEATURES = [
  { featureName: 'Assisted eKYC Request', featureCode: 'ASSISTED_EKYC_REQUEST' },
  { featureName: 'FDR/DPS Account Opening Request', featureCode: 'FDR_ACCOUNT_OPENING_REQUEST' },
];

export const AUDIT_BRANCHES = [
  { name: 'Motijheel Branch', branchCode: '0101' },
  { name: 'Gulshan Branch', branchCode: '0102' },
  { name: 'Bijoy Nagar Branch', branchCode: '0057' },
  { name: 'Dilkusha Islamic Banking Branch', branchCode: '0072' },
  { name: 'Agrabad Branch', branchCode: '0211' },
];

export const AUDIT_USERS = [
  { fullName: 'Md. Rahman Ali', userName: 'maker.rahman' },
  { fullName: 'Nusrat Islam', userName: 'checker.islam' },
  { fullName: 'Zihad Hasan', userName: 'cogb.hasan' },
  { fullName: 'Farhana Sultana', userName: 'admin.sultana' },
  { fullName: 'Abdul Karim', userName: 'maker.karim' },
];

const OPERATIONS = ['CREATE', 'UPDATE'];
const STATUSES = ['APPROVED', 'MAKER_REQUESTED', 'APPROVAL_PROCESSING', 'REJECTED'];

const CHECKER_COMMENTS: Record<string, string> = {
  APPROVED: 'Verified against the supporting documents.',
  MAKER_REQUESTED: '',
  APPROVAL_PROCESSING: 'Forwarded for final approval.',
  REJECTED: 'Details did not match the request.',
};

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

function stamp(date: Date): string {
  return (
    `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())} ` +
    `${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}`
  );
}

/**
 * Deterministic rows so paging and filtering behave the same across reloads. Newest first.
 *
 * Each dimension cycles on a different period so they do not correlate: every Action Type ×
 * Status combination and every Maker × Branch pair is reachable, so no filter combination the
 * UI offers can come back empty.
 */
export function buildAuditLog(count = 48): (IAuditLog & Record<string, any>)[] {
  const base = new Date('2026-09-03T11:45:00Z').getTime();

  return Array.from({ length: count }, (_, index) => {
    const maker = AUDIT_USERS[index % AUDIT_USERS.length];
    const checker = AUDIT_USERS[(index + 1) % AUDIT_USERS.length];
    const approver = AUDIT_USERS[(index + 2) % AUDIT_USERS.length];
    const feature = AUDIT_FEATURES[index % AUDIT_FEATURES.length];
    const branch = AUDIT_BRANCHES[Math.floor(index / AUDIT_USERS.length) % AUDIT_BRANCHES.length];
    const operation = OPERATIONS[index % OPERATIONS.length];
    const status = STATUSES[Math.floor(index / OPERATIONS.length) % STATUSES.length];

    const operatedAt = new Date(base - index * 41 * 60 * 1000);
    const checkedAt = new Date(operatedAt.getTime() + 22 * 60 * 1000);
    const approvedAt = new Date(operatedAt.getTime() + 65 * 60 * 1000);
    const settled = status === 'APPROVED' || status === 'REJECTED';

    return {
      id: count - index,
      makerName: maker.fullName,
      createdBy: maker.userName,
      operationDate: stamp(operatedAt),
      feature: feature.featureName,
      featureCode: feature.featureCode,
      operation,
      status,
      branch: branch.branchCode,
      branchName: branch.name,
      checkerName: status === 'MAKER_REQUESTED' ? null : checker.fullName,
      checkerDate: status === 'MAKER_REQUESTED' ? null : stamp(checkedAt),
      checkerComment: CHECKER_COMMENTS[status] || null,
      approverName: settled ? approver.fullName : null,
      approverDate: settled ? stamp(approvedAt) : null,
      approverComment: settled ? 'Reviewed and closed.' : null,
      displayData: JSON.stringify({
        Feature: feature.featureName,
        Operation: operation,
        'Requested By': maker.fullName,
        Branch: branch.name,
        Reference: `AUD-${String(900000 + index * 13).slice(0, 6)}`,
      }),
      remarksDetails:
        operation === 'UPDATE'
          ? JSON.stringify([
              { fieldName: 'Status', existingValue: 'INACTIVE', updatedValue: 'ACTIVE' },
              { fieldName: 'Branch', existingValue: AUDIT_BRANCHES[0].name, updatedValue: branch.name },
            ])
          : `${operation} performed on ${feature.featureName}.`,
    };
  });
}
